// Shared file-presentation helpers used by the in-context entity-documents card
// and the org-wide documents repository (table icon chip, preview drawer, delete
// dialog). One source of truth so the icon labels / colors / byte formatting
// stay identical across every documents surface.

/** Format a raw byte count into a human-readable string (e.g. "1.2 MB"). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const clamped = Math.min(i, units.length - 1);
  const value = bytes / Math.pow(1024, clamped);
  return `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[clamped]}`;
}

/** Return a short uppercase label for a MIME type (e.g. "PDF", "JPG"). */
export function mimeLabel(contentType: string): string {
  const map: Record<string, string> = {
    "application/pdf": "PDF",
    "image/jpeg": "JPG",
    "image/png": "PNG",
    "image/gif": "GIF",
    "image/webp": "WEBP",
    "image/svg+xml": "SVG",
    "text/plain": "TXT",
    "text/csv": "CSV",
    "application/vnd.ms-excel": "XLS",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
    "application/msword": "DOC",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  };
  return map[contentType] ?? contentType.split("/").pop()?.toUpperCase().slice(0, 6) ?? "FILE";
}

/** Tailwind background class by file-type label for the square icon chip. */
export function mimeColor(label: string): string {
  const colors: Record<string, string> = {
    PDF: "bg-red-500",
    JPG: "bg-purple-500",
    PNG: "bg-purple-500",
    GIF: "bg-purple-500",
    WEBP: "bg-purple-400",
    SVG: "bg-purple-400",
    TXT: "bg-gray-500",
    CSV: "bg-green-600",
    XLS: "bg-green-700",
    XLSX: "bg-green-700",
    DOC: "bg-blue-700",
    DOCX: "bg-blue-700",
  };
  return colors[label] ?? "bg-gray-500";
}

/** True when the content type can render inline in the preview drawer. */
export function isPreviewable(contentType: string): boolean {
  return contentType === "application/pdf" || contentType.startsWith("image/");
}
